<template>
  <div class="mx-auto flex w-full max-w-3xl flex-col gap-2.5 py-5">
    <template v-if="isLoading">
      <div v-for="i in 3" :key="'team-skeleton-' + i" class="glass-tile flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl p-4">
        <ui-skeleton class="size-9 rounded-full" />
        <!-- Line boxes and widths as in the loaded row, so nothing shifts. -->
        <div class="flex min-w-0 flex-1 flex-col">
          <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-40" /></span>
          <span class="flex h-5 items-center"><ui-skeleton class="h-3 w-56 max-w-full" /></span>
        </div>
        <div class="flex items-center gap-1.5 max-sm:w-full">
          <ui-skeleton class="h-9 max-sm:flex-1 sm:w-48" />
          <span class="size-9 shrink-0" />
          <span class="size-9 shrink-0" />
        </div>
      </div>
    </template>

    <template v-else>
      <div v-for="user in users" :key="user.id" class="glass-tile flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl p-4">
        <ui-avatar class="size-9">
          <ui-avatar-fallback class="text-xs font-semibold">{{ initials(user.fullname) }}</ui-avatar-fallback>
        </ui-avatar>
        <div class="flex min-w-0 flex-1 flex-col">
          <span class="truncate text-sm font-medium text-foreground">{{ user.fullname }}</span>
          <span class="truncate text-sm text-muted-foreground">{{ user.email }}</span>
        </div>

        <div class="flex items-center gap-1.5 max-sm:w-full">
          <span v-tooltip="levelChangeTooltip(user) || null" class="max-sm:flex-1">
            <ui-select
              :model-value="tmpUserLevels[user.id]"
              :disabled="disabledLevelChange(user)"
              @update:model-value="(value) => { tmpUserLevels[user.id] = value; onChangeUserLevel(user); }"
            >
              <ui-select-trigger class="w-full sm:w-48" :aria-label="tr('Access level')">
                <ui-select-value />
              </ui-select-trigger>
              <ui-select-content>
                <ui-select-item v-for="level in levelList(user)" :key="level.value" :value="level.value">
                  {{ level.label }}
                </ui-select-item>
              </ui-select-content>
            </ui-select>
          </span>

          <ui-button
            v-if="isRestrictedLevel(tmpUserLevels[user.id])"
            v-tooltip="tr('Edit restrictions')"
            variant="ghost"
            size="icon"
            :aria-label="tr('Edit restrictions')"
            :disabled="userBeingUpdated !== null"
            @click="() => openEditUserRestrictionsModal(user, tmpUserLevels[user.id])"
          >
            <pencil-icon />
          </ui-button>
          <span v-else class="size-9 shrink-0" />

          <span v-tooltip="deleteButtonTooltip(user) || null">
            <ui-button
              variant="ghost"
              size="icon"
              class="text-destructive hover:text-destructive"
              :aria-label="tr('Delete user')"
              :disabled="userBeingUpdated !== null || $root.loggedInUser.id === user.id || !canDeleteUser(user)"
              @click="() => openUserDeleteConfirmModal(user)"
            >
              <loader-circle-icon v-if="userBeingUpdated === user.id" class="animate-spin" />
              <trash-2-icon v-else />
            </ui-button>
          </span>
        </div>
      </div>

      <div v-for="invite in invites" :key="invite.id" class="glass-tile flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl p-4">
        <ui-avatar class="size-9 opacity-60">
          <ui-avatar-fallback class="text-xs font-semibold">{{ initials(invite.firstname + ' ' + invite.lastname) }}</ui-avatar-fallback>
        </ui-avatar>
        <div class="flex min-w-0 flex-1 flex-col">
          <span class="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
            {{ (invite.firstname + ' ' + invite.lastname).trim() }}
            <ui-badge variant="secondary" class="font-normal">{{ tr('Pending invitation') }}</ui-badge>
          </span>
          <span class="truncate text-sm text-muted-foreground">{{ invite.email }}</span>
        </div>

        <div class="flex items-center gap-1.5 max-sm:w-full">
          <ui-button
            variant="outline"
            :disabled="resendInvitationId !== null || revokeInvitationId !== null"
            class="max-sm:flex-1"
            @click="() => resendInvitation(invite)"
          >
            <loader-circle-icon v-if="resendInvitationId === invite.id" class="animate-spin" />
            <send-icon v-else />
            {{ tr('Resend') }}
          </ui-button>
          <span class="inline-flex h-9 w-48 items-center rounded-md px-3 text-sm text-muted-foreground max-sm:hidden">
            {{ levelLabel(invite.level) }}
          </span>
          <ui-button
            variant="ghost"
            size="icon"
            class="text-destructive hover:text-destructive"
            :aria-label="tr('Revoke invitation')"
            :disabled="resendInvitationId !== null || revokeInvitationId !== null"
            @click="() => revokeInvitation(invite)"
          >
            <loader-circle-icon v-if="revokeInvitationId === invite.id" class="animate-spin" />
            <trash-2-icon v-else />
          </ui-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { LoaderCircle, Pencil, Send, Trash2 } from '@lucide/vue';

import CompanyService from '@/services/Company.js';
import UserService from '@/services/User.js';
import EditUserRestrictionsModal from '@/ui/components/EditUserRestrictionsModal.vue';

export default {
  name: 'SettingsTeamView',
  components: {
    LoaderCircleIcon: LoaderCircle,
    PencilIcon: Pencil,
    SendIcon: Send,
    Trash2Icon: Trash2,
  },
  data:() => ({
    isLoading: true,
    users: [],
    tmpUserLevels: {},
    userBeingUpdated: null,
    resendInvitationId: null,
    revokeInvitationId: null,
  }),
  computed: {
    loggedInUserCompany() {
      return this.$root.currentCompany;
    },
    invites() {
      return this.$store.getters['invites/list'];
    },
  },
  watch: {
    users: {
      immediate: true,
      handler() {
        const newLevels = {};
        this.users.forEach(user => newLevels[user.id] = user.level);
        this.tmpUserLevels = newLevels;
      },
    },
  },
  created() {
    this.updateContent();
  },
  methods: {
    initials(name) {
      return String(name || '').trim().split(/\s+/).map(part => part[0]).slice(0, 2).join('').toUpperCase();
    },
    isRestrictedLevel(level) {
      return level === 'restricted_viewer' || level === 'restricted_editor';
    },
    levelLabel(level) {
      return this.levelList({ level }).find(item => item.value === level)?.label ?? level;
    },
    levelList(user) {
      const levels = [
        { label: this.tr('Admin'), value: 'admin' },
        { label: this.tr('Normal'), value: 'normal' },
        { label: this.tr('Restricted Editor'), value: 'restricted_editor' },
        { label: this.tr('Restricted Viewer'), value: 'restricted_viewer' },
      ];
      if (this.isCurrentUserOwner() || this.disabledLevelChange(user)) {
        levels.unshift({ label: this.tr('Owner'), value: 'owner' });
      }
      return levels;
    },
    updateContent() {
      this.isLoading = true;

      const promises = [
        CompanyService.getMembers(this.loggedInUserCompany.id).then(users => this.users = users),
        this.$store.dispatch('invites/fetch'),
      ];

      Promise.all(promises)
        .then(() => this.isLoading = false)
        .catch(err => this.error(err));
    },
    resendInvitation(invite) {
      if (this.resendInvitationId !== null) {
        return;
      }
      this.resendInvitationId = invite.id;

      this.$store
        .dispatch('invites/resendInvitation', invite)
        .then(resp => {
          this.$toast.success(this.tr('Invitation has been sent successfully.'));

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.resendInvitationId = null);
    },
    revokeInvitation(invite) {
      if (this.revokeInvitationId !== null) {
        return;
      }
      this.revokeInvitationId = invite.id;

      this.$store
        .dispatch('invites/deleteInvitation', invite)
        .then(resp => {
          this.$toast.success(this.tr('Invitation has been revoked successfully.'));

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.revokeInvitationId = null);
    },

    onChangeUserLevel(user) {
      const newLevel = this.tmpUserLevels[user.id];

      if (newLevel === 'restricted_viewer' || newLevel === 'restricted_editor') {
        const backupMethod = () => {
          this.tmpUserLevels[user.id] = user.level;
        };

        this.openEditUserRestrictionsModal(user, newLevel, backupMethod);
      }
      else {
        this.changeUserLevel(user, newLevel);
      }
    },

    openEditUserRestrictionsModal(user, newLevel, backupMethod = null) {
      let saved = false;

      const modalInstance = this.$fullscreenModal.open(
        this,
        EditUserRestrictionsModal,
        {
          user,
          newLevel,
        },
        {
          componentListeners: {
            save: updatedUser => {
              saved = true;
              user.level = updatedUser.level;
              user.visible_brand_ids = updatedUser.visible_brand_ids;
            },
          },
        },
      );

      modalInstance.$on('closed', () => {
        if (!saved && backupMethod) {
          backupMethod();
        }
      });
    },

    changeUserLevel(user, newLevel) {
      this.userBeingUpdated = user.id;

      UserService.changeLevel(user.id, { level: newLevel })
        .then(resp => {
          user.level = newLevel;

          this.$toast.success(this.tr('The user\'s access level has been successfully changed.'));

          return resp;
        })
        .catch(err => {
          this.error(err);
          this.tmpUserLevels[user.id] = user.level;
        })
        .finally(() => this.userBeingUpdated = null);
    },
    openUserDeleteConfirmModal(user) {
      this.$confirmation.open({
        title: this.tr('Delete user'),
        content: this.tr('Do you really want to delete :name\'s user account?', { ':name': user.fullname }) + '<br>' + this.tr('This action cannot be undone.'),
        labelCancel: this.tr('Cancel'),
        labelConfirm: this.tr('Confirm'),
        destructive: true,
        confirm: () => this.removeUser(user),
      });
    },
    removeUser(user) {
      return UserService.delete(user.id)
        .then(resp => {
          this.updateContent();
          this.$store.dispatch('auth/fetch');

          this.$toast.clear();
          this.$toast.success(this.tr('User was successfully deleted.'));

          return resp;
        })
        .catch(err => this.error(err));
    },
    disabledLevelChange(user) {
      return this.userBeingUpdated !== null || this.$root.loggedInUser.id === user.id || !this.canChangeLevelOf(user);
    },
    canChangeLevelOf(user) {
      if (user.level === 'owner' && !this.isCurrentUserOwner()) {
        return false;
      }
      return true;
    },
    isCurrentUserOwner() {
      return this.$root.loggedInUser?.level === 'owner';
    },
    canDeleteUser(user) {
      if (user.id === this.$root.loggedInUser?.id) {
        return false;
      }
      if (user.level === 'owner') {
        return this.isCurrentUserOwner();
      }
      return true;
    },
    levelChangeTooltip(user) {
      if (user.level === 'owner' && !this.isCurrentUserOwner()) {
        return this.tr('Only an owner can change another owner\'s level.');
      }
      return '';
    },
    deleteButtonTooltip(user) {
      if (user.level === 'owner' && !this.isCurrentUserOwner()) {
        return this.tr('Only an owner can delete another owner.');
      }
      return '';
    },
  },
};
</script>
